<template>
  <!-- Ionic modal stays inside ion-app for proper card presentation -->
  <!-- Keep mounted while transitioning away to allow proper dismiss animation -->
  <ModalsContainerIonic
    v-if="shouldUseIonicModal || isTransitioningFromIonic"
    :open="storeModals.isModalOpen && shouldUseIonicModal"
    :type="ionicModalType"
    :modal-props="ionicModalProps"
    :can-dismiss="storeModals.modalCanDismiss"
    @close="handleClose"
    @did-dismiss="handleIonicDidDismiss" />

  <!-- Non-Ionic modals teleport to body for z-index layering -->
  <Teleport to="body">
    <div v-if="!shouldUseIonicModal && !isTransitioningFromIonic" class="spectra-modals-container">
      <!-- Use simple modal for default presentation -->
      <ModalContainerSimple
        :open="storeModals.isModalOpen"
        :type="storeModals.modalType"
        :modal-props="storeModals.modalProps"
        :can-dismiss="storeModals.modalCanDismiss"
        :presentation="storeModals.modalPresentation"
        @close="handleClose" />

      <!-- Child modals (stacked) - always use simple modal -->
      <template v-for="(modal, index) in storeModals.childModals" :key="index">
        <ModalContainerSimple
          :open="true"
          :type="modal.type"
          :modal-props="modal.props"
          :can-dismiss="modal.canDismiss"
          @close="modal.handleClose" />
      </template>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, watch, ref, inject } from 'vue'
import { useStoreAuth } from '@/stores/storeAuth'
import { useRoute } from 'vue-router'
import { useStoreProfile } from '@/stores/storeProfile'
import { useStoreModals } from '@/stores/storeModals'
import ModalContainerSimple from '@/components/ModalContainerSimple.vue'
import ModalsContainerIonic from '@/components/ModalsContainerIonic.vue'

const enableWelcome = true

const { isMobileApp } = inject('platform')
const storeAuth = useStoreAuth()
const storeModals = useStoreModals()
const storeProfile = useStoreProfile()
const route = useRoute()
const profile = computed(() => storeProfile.getCurrentUserProfile)

// Track when we're transitioning away from Ionic modal
// This prevents yanking the Ionic modal from DOM before it can properly dismiss
const isTransitioningFromIonic = ref(false)

// Cache the Ionic modal info while it's active
// This is used during the close transition to avoid flashing different content
const cachedIonicModalType = ref('')
const cachedIonicModalProps = ref({})

// Use Ionic modal for fullscreen presentation on mobile (iOS card style)
// Review modals only use Ionic card modal when the fullscreen_modals preference is enabled
const shouldUseIonicModal = computed(() => {
  if (!isMobileApp.value) return false
  if (storeModals.modalPresentation !== 'fullscreen') return false

  const modalType = storeModals.modalType
  if (modalType === 'ReviewCreate' || modalType === 'ReviewEdit') {
    return storeProfile.getCurrentUserProfile?.feature_flags?.fullscreen_modals === true
  }
  return true
})

// Computed values for the Ionic modal - use cached during transition
const ionicModalType = computed(() => {
  if (isTransitioningFromIonic.value) {
    return cachedIonicModalType.value
  }
  return storeModals.modalType
})

const ionicModalProps = computed(() => {
  if (isTransitioningFromIonic.value) {
    return cachedIonicModalProps.value
  }
  return storeModals.modalProps
})

// Keep the cache updated while Ionic modal is active
watch(
  () => ({ type: storeModals.modalType, props: storeModals.modalProps }),
  (newVal) => {
    if (shouldUseIonicModal.value && !isTransitioningFromIonic.value) {
      cachedIonicModalType.value = newVal.type
      cachedIonicModalProps.value = { ...newVal.props }
    }
  },
  { immediate: true, deep: true }
)

// Clean up presenting element transforms thoroughly
// Ionic card modals apply transforms to the presenting element (ion-router-outlet)
// These must be cleaned up when the modal closes
const cleanupPresentingElementTransforms = () => {
  const routerOutlet = document.querySelector('ion-router-outlet')
  if (routerOutlet) {
    // Clear inline styles
    routerOutlet.style.transform = ''
    routerOutlet.style.transformOrigin = ''
    routerOutlet.style.transition = ''

    // Clear transforms on all ion-page elements inside
    const pages = routerOutlet.querySelectorAll('.ion-page')
    pages.forEach(page => {
      page.style.transform = ''
      page.style.transformOrigin = ''
      page.style.transition = ''
    })

    // Also check ion-content elements
    const contents = routerOutlet.querySelectorAll('ion-content')
    contents.forEach(content => {
      content.style.transform = ''
      content.style.transformOrigin = ''
    })
  }
}

// Handle when Ionic modal finishes its dismiss animation
const handleIonicDidDismiss = () => {
  // Clean up transforms
  cleanupPresentingElementTransforms()

  // End the transition state
  isTransitioningFromIonic.value = false
  cachedIonicModalType.value = ''
  cachedIonicModalProps.value = {}
}

watch(shouldUseIonicModal, (newVal, oldVal) => {
  // When transitioning from Ionic modal to simple modal,
  // we need to keep the Ionic modal mounted briefly so it can
  // properly animate out and clean up its transforms
  if (oldVal === true && newVal === false) {
    // The cached values are already set from the previous watcher
    // Just mark that we're transitioning
    isTransitioningFromIonic.value = true

    // Safety timeout - if did-dismiss doesn't fire, clean up anyway
    // Ionic modal dismiss animation is ~300ms
    setTimeout(() => {
      if (isTransitioningFromIonic.value) {
        cleanupPresentingElementTransforms()
        isTransitioningFromIonic.value = false
        cachedIonicModalType.value = ''
        cachedIonicModalProps.value = {}
      }
    }, 500)
  }
})

const userNeedsWelcomeOrOnboarding = computed(() => {
  const needsWelcome = profile.value?.needs_welcome
  const needsOnboarding = profile.value?.needs_onboarding
  return needsWelcome || needsOnboarding
})
// Auth wall computed properties no longer needed - handled by router guards
// const userIsNotAuthed = computed(() => storeAuth.userNotAuthed)
// const userIsAuthed = computed(() => storeAuth.user !== null)
// const userNeedsAuthWall = computed(() => {
//   if (
//     route.meta.requiresAuth &&
//     userIsNotAuthed.value === true &&
//     !userIsAuthed.value
//   ) {
//     return true
//   }
//   return false
// })

const handleClose = () => {
  storeModals.closeModal()
}

const handleMaybeShowWelcome = () => {
  if (!enableWelcome) return

  // If user has completed onboarding, don't show the modal
  if (storeProfile.isOnboarded) {
    return
  }

  // Only skip if we've already opened the modal AND user is onboarded
  // Don't use session flag alone - if onboarding isn't complete, always try to show
  if (storeProfile.currentSessionSeenWelcome === false) {
    storeModals.openModal({
      type: 'AppWelcome',
      props: {},
      canDismiss: false
    })
    // Don't set currentSessionSeenWelcome here - it will be set when onboarding completes
    // This allows the modal to reopen if it closes prematurely (e.g., due to OTA update)
  }
}

watch(
  userNeedsWelcomeOrOnboarding,
  (newVal) => {
    if (newVal) {
      handleMaybeShowWelcome()
    }
  },
  { immediate: true }
)

// Track if the welcome modal was open (to detect premature close)
const wasWelcomeModalOpen = ref(false)

// Watch for modal type to track when welcome modal is open
watch(
  () => storeModals.modalType,
  (type) => {
    if (type === 'AppWelcome') {
      wasWelcomeModalOpen.value = true
    }
  }
)

// Watch for modal close - if welcome modal closes while user still needs onboarding, reopen it
// This handles cases where the modal is closed prematurely (e.g., OTA update, navigation event)
watch(
  () => storeModals.isModalOpen,
  (isOpen, wasOpen) => {
    // Only act when modal closes (wasOpen: true -> isOpen: false)
    if (wasOpen && !isOpen && wasWelcomeModalOpen.value) {
      // Reset the tracking flag
      wasWelcomeModalOpen.value = false

      // Check if user still needs onboarding and hasn't completed it
      if (userNeedsWelcomeOrOnboarding.value && !storeProfile.isOnboarded) {
        // Small delay to let any other close handlers complete
        setTimeout(() => {
          // Double-check the condition is still true
          if (userNeedsWelcomeOrOnboarding.value && !storeProfile.isOnboarded) {
            handleMaybeShowWelcome()
          }
        }, 500)
      }
    }
  }
)

// Auth wall has been disabled - authentication is now handled via route guards
// const authWallTimeout = ref(null)
// const authWallTimeoutDuration = 1000

// watch(
//   userNeedsAuthWall,
//   (newVal) => {
//     // Auth wall disabled - users are redirected to login via router instead
//   },
//   { immediate: true }
// )
</script>
